// ЦЕНЫ В ПРОЕКТЕ — ЭТО СТРОКИ, А НЕ ЧИСЛА.
//
// В админке цена вводится как есть: «2600 $», «75 000 ₸», «от 20 000 ₸».
// Так и задумано: менеджер пишет привычным способом, а не заполняет три поля
// (сумма / валюта / формат). Но калькулятор опций на странице тура должен
// эти строки СКЛАДЫВАТЬ — значит, число из строки надо достать, а потом
// собрать итог обратно ровно в том же виде, в каком его написал менеджер.
//
// Здесь этим занимаются две функции:
//   parsePrice("2600 $")  → { amount: 2600, prefix: "", suffix: " $", … }
//   formatPrice(2750, …)  → "2 750 $"
//
// Если строку разобрать не удалось (например «договорная»), amount === null —
// вызывающий код это проверяет и просто не показывает итог.

// Пробелы бывают разные: обычный, неразрывный (U+00A0) и узкий неразрывный
// (U+202F). В цене «75 000 ₸» из Word почти всегда стоит неразрывный.
const SPACES = /[\s  ]/g;
// Отдельный экземпляр без флага g: у глобальной регулярки .test() помнит
// позицию между вызовами и через раз возвращает false.
const HAS_SPACE = /[\s  ]/;

// Число внутри строки. Разряды могут быть отбиты чем угодно — пробелом
// («75 000»), запятой («2,600») или точкой («2.600»), — поэтому все три знака
// входят в разделитель, а разбирает их уже toNumber.
const NUMBER = /\d+(?:[  .,]\d+)*/;

// «1,250» и «1.250» — это тысячи, а «12,50» и «12.50» — копейки.
// Отличаем по длине хвоста: три цифры после разделителя = группа тысяч.
const toNumber = (raw) => {
  const cleaned = raw.replace(SPACES, "");
  // хвост из ровно трёх цифр после последнего разделителя → разделитель групп
  const groupSeparated = /^\d{1,3}(?:([.,])\d{3})+$/.exec(cleaned);
  if (groupSeparated) return Number(cleaned.split(groupSeparated[1]).join(""));

  // остальное: последний разделитель считаем десятичным, прочие — групповыми
  const lastSep = Math.max(cleaned.lastIndexOf(","), cleaned.lastIndexOf("."));
  if (lastSep === -1) return Number(cleaned);
  const whole = cleaned.slice(0, lastSep).replace(/[.,]/g, "");
  const fraction = cleaned.slice(lastSep + 1);
  return Number(`${whole}.${fraction}`);
};

/**
 * Разобрать строку цены.
 *
 * Возвращает { amount, prefix, suffix, grouped, raw }:
 *   amount  — число (null, если цифр в строке нет);
 *   prefix  — всё, что стояло ДО числа («$», «от »);
 *   suffix  — всё, что стояло ПОСЛЕ («  $», « ₸», « за человека»);
 *   grouped — были ли в исходной строке разряды через пробел («75 000»),
 *             чтобы итог выглядел так же.
 */
export const parsePrice = (raw) => {
  const source = raw == null ? "" : String(raw);
  const match = NUMBER.exec(source);
  if (!match) {
    return { amount: null, prefix: "", suffix: "", grouped: false, raw: source };
  }

  return {
    amount: toNumber(match[0]),
    prefix: source.slice(0, match.index),
    suffix: source.slice(match.index + match[0].length),
    grouped: HAS_SPACE.test(match[0]) || /\d[\s  ]\d/.test(source),
    raw: source,
  };
};

/** Число в строку с неразрывными пробелами по разрядам: 2750 → «2 750». */
const groupDigits = (value) => {
  const [whole, fraction] = String(value).split(".");
  const spaced = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return fraction ? `${spaced},${fraction}` : spaced;
};

/**
 * Собрать цену обратно в строку по образцу исходной.
 *
 *   formatPrice(2750, parsePrice("2600 $"))     → "2750 $"
 *   formatPrice(80000, parsePrice("75 000 ₸"))  → "80 000 ₸"
 *
 * `prefix` из образца сознательно НЕ переносим, если это слово «от»:
 * итог посчитан по конкретным опциям, он уже не «от».
 */
export const formatPrice = (amount, template = {}) => {
  if (amount == null || Number.isNaN(amount)) return "";
  const rounded = Math.round(amount * 100) / 100;
  const digits = template.grouped ? groupDigits(rounded) : String(rounded);
  const prefix = /^\s*(от|from|бастап)\s*$/i.test(template.prefix || "")
    ? ""
    : template.prefix || "";
  return `${prefix}${digits}${template.suffix || ""}`.trim();
};

/**
 * Цена опции: число, «бесплатно» (0) или «по запросу» (в строке нет цифр).
 * Возвращает { amount, hasPrice, free, display }.
 */
export const optionPrice = (raw) => {
  const parsed = parsePrice(raw);
  return {
    amount: parsed.amount ?? 0,
    hasPrice: parsed.amount != null,
    free: parsed.amount === 0,
    display: parsed.raw.trim(),
    template: parsed,
  };
};
